/**
 * Admin brief hooks — the data layer behind every editor page.
 *
 * One brief per tenant, edited in slices (basics / team / services / …).
 * Each editor reads the full brief via `useBrief()`, edits its slice, then
 * calls `useUpdateBriefSlice()` which performs a JSON-patch-style merge
 * client-side and PUTs the whole result back.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export const BRIEF_QUERY_KEY = ['admin', 'brief'] as const;
export const ME_QUERY_KEY = ['admin', 'me'] as const;

/** Loose brief shape — the editors narrow this further per section. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Brief = Record<string, any>;

export interface MeInfo {
  user_id: string;
  role: string;
  tenant: {
    id: string;
    slug: string;
    legal_name: string;
    tier: string;
    status: string;
    preview_version: number;
    live_version: number | null;
    preview_token: string;
    custom_host: string | null;
  };
}

export function useMe() {
  return useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: async (): Promise<MeInfo> => {
      const res = await api.api.admin.me.$get();
      if (!res.ok) throw new Error('failed_to_load_me');
      return (await res.json()) as MeInfo;
    },
    staleTime: 60_000,
  });
}

export function useBrief() {
  return useQuery({
    queryKey: BRIEF_QUERY_KEY,
    queryFn: async (): Promise<Brief> => {
      const res = await api.api.admin.brief.$get();
      if (!res.ok) throw new Error('failed_to_load_brief');
      const json = (await res.json()) as { brief: Brief; hash: string | null };
      return json.brief;
    },
    staleTime: 30_000,
  });
}

/**
 * Update one or more top-level slices of the brief in a single PUT.
 * Server validates schema, hashes, persists. Cache updates optimistically.
 *
 * Usage:
 *   const update = useUpdateBriefSlice();
 *   await update.mutateAsync({ slice: 'company', data: {...} });
 *   await update.mutateAsync({ patch: { company: {...}, address: {...} } });
 */
export function useUpdateBriefSlice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: { slice: string; data: unknown } | { patch: Record<string, unknown> }
    ) => {
      const current = (qc.getQueryData<Brief>(BRIEF_QUERY_KEY) ?? {}) as Brief;
      const patch: Record<string, unknown> =
        'patch' in input ? input.patch : { [input.slice]: input.data };
      const merged: Brief = { ...current, ...patch };
      const res = await api.api.admin.brief.$put({ json: merged });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error ?? 'update_failed');
      }
      return { ok: true, merged };
    },
    onSuccess: ({ merged }) => {
      qc.setQueryData(BRIEF_QUERY_KEY, merged);
    },
  });
}

/** Trigger preview build (server enqueues; build-runner picks up + executes). */
export function useRequestPreview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.api.admin.brief.preview.$post();
      if (!res.ok) throw new Error('preview_request_failed');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: BUILD_STATUS_QUERY_KEY });
    },
  });
}

export const BUILD_STATUS_QUERY_KEY = ['admin', 'build-status'] as const;

export interface BuildStatusRow {
  tenant_id: string;
  version: number;
  brief_hash: string;
  generated_at: number;
  generator: string;
  llm_model: string | null;
  page_count: number;
  status: 'requested' | 'building' | 'ready' | 'failed';
  error: string | null;
}

export function useBuildStatus() {
  return useQuery({
    queryKey: BUILD_STATUS_QUERY_KEY,
    queryFn: async (): Promise<{ latest: BuildStatusRow | null; recent: BuildStatusRow[] }> => {
      const res = await api.api.admin.brief['build-status'].$get();
      if (!res.ok) throw new Error('failed_to_load_build_status');
      return (await res.json()) as { latest: BuildStatusRow | null; recent: BuildStatusRow[] };
    },
    // Poll every 10s while a build is active. Static otherwise.
    refetchInterval: (query) => {
      const data = query.state.data as { latest: BuildStatusRow | null } | undefined;
      if (
        data?.latest &&
        (data.latest.status === 'requested' || data.latest.status === 'building')
      ) {
        return 10_000;
      }
      return false;
    },
  });
}

export function usePromote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.api.admin.brief.promote.$post();
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error ?? 'promote_failed');
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_QUERY_KEY }),
  });
}

export function useRollback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.api.admin.brief.rollback.$post();
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error ?? 'rollback_failed');
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_QUERY_KEY }),
  });
}

export const VERSION_HISTORY_QUERY_KEY = ['admin', 'versions'] as const;

export interface VersionHistoryRow {
  tenant_id: string;
  version: number;
  brief_hash: string;
  generated_at: number;
  generator: string;
  llm_model: string | null;
  page_count: number;
  status: 'requested' | 'building' | 'ready' | 'failed';
  error: string | null;
}

export interface VersionHistoryResponse {
  tenant: {
    live_version: number | null;
    preview_version: number;
    preview_token: string;
    custom_host: string | null;
    slug: string;
  };
  versions: VersionHistoryRow[];
}

export function useVersionHistory() {
  return useQuery({
    queryKey: VERSION_HISTORY_QUERY_KEY,
    queryFn: async (): Promise<VersionHistoryResponse> => {
      const res = await api.api.admin.versions.$get();
      if (!res.ok) throw new Error('failed_to_load_versions');
      return (await res.json()) as VersionHistoryResponse;
    },
    refetchInterval: (query) => {
      const data = query.state.data as VersionHistoryResponse | undefined;
      const pending = (data?.versions ?? []).some(
        (v) => v.status === 'requested' || v.status === 'building'
      );
      return pending ? 10_000 : false;
    },
  });
}

export function usePinVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (version: number) => {
      const res = await api.api.admin.versions[':version'].pin.$post({
        param: { version: String(version) },
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error ?? 'pin_failed');
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
      qc.invalidateQueries({ queryKey: VERSION_HISTORY_QUERY_KEY });
    },
  });
}
